import { Injectable } from '@angular/core';
import { Router, NavigationEnd, RoutesRecognized } from '@angular/router';
import { filter, scan, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from 'environments/environment';

declare const ga;

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private stopTrackingSubject: Subject<void> = new Subject();

  constructor(private router: Router) {}

  public startTracking() {
    if (environment.googleAnalyticsTrackId) {
      ga('create', environment.googleAnalyticsTrackId, 'auto');
      this.listenToPageChanges();
    }
  }

  public stopTracking() {
    this.stopTrackingSubject.next();
  }

  public trackClickLink(linkLabel: string) {
    this.trackEvent('Link', 'Click', linkLabel);
  }

  public trackEvent(
    eventCategory: string,
    eventAction: string,
    eventLabel: string
  ) {
    if (environment.googleAnalyticsTrackId) {
      ga('send', 'event', eventCategory, eventAction, eventLabel);
    } else {
      console.log('Tracking event:', eventCategory, eventAction, eventLabel);
    }
  }

  private listenToPageChanges() {
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd || event instanceof RoutesRecognized
        ),
        scan<RoutesRecognized & { navigationEnded: boolean; endUrl?: string }>(
          (acc, event: NavigationEnd | RoutesRecognized) => {
            if (event instanceof RoutesRecognized) {
              return { ...event, navigationEnded: false };
            } else {
              return {
                ...acc,
                navigationEnded: true,
                endUrl: event.urlAfterRedirects
              };
            }
          }
        ),
        filter(event => event.navigationEnded),
        map(event => {
          let root = event.state.root;
          const params = root.paramMap.keys.map(key => [
            key,
            root.paramMap.get(key)
          ]);
          while (root.firstChild) {
            root = root.firstChild;
            root.paramMap.keys.forEach(key =>
              params.push([key, root.paramMap.get(key)])
            );
          }

          return params.reduce(
            (url, [key, value]) => url.replace(value, key),
            event.urlAfterRedirects
          );
        }),
        takeUntil(this.stopTrackingSubject)
      )
      .subscribe(url => {
        ga('set', 'page', url);
        ga('send', 'pageview');
      });
  }
}
